import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Mail, User, Calendar, CheckCircle2, 
  Lock, Loader2, AlertCircle 
} from 'lucide-react';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchInvitationDetails();
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_URL}/api/public/accept-invitation/${token}`
      );
      
      setInvitation(response.data);
    } catch (error) {
      console.error('Failed to fetch invitation:', error);
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();

    // Validation
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setAccepting(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/public/accept-invitation/accept`,
        {
          invitation_token: token,
          admin_password: password
        }
      );

      if (response.data.success) {
        toast.success('🎉 Welcome! Your clinic is ready!');
        
        // Store auth data
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem(`first_login_${response.data.user.id}`, 'true');
        
        // Redirect to admin dashboard
        setTimeout(() => {
          window.location.href = '/admin';
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
              <p className="text-gray-600">Loading invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="bg-red-100 p-4 rounded-full mb-4">
                <AlertCircle className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Invalid Invitation
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/')}>
                Return to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center">
          <Logo />
          <div className="ml-2 flex flex-col">
            <span className="text-xl font-bold text-gray-900">MyMedMember</span>
            <span className="text-xs text-gray-500 -mt-0.5">by Clinicax Systems</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Invitation Card */}
        <Card className="mb-6 border-2 border-purple-200">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-full">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-2xl">You're Invited!</CardTitle>
                <CardDescription className="text-white/90">
                  Welcome to MyMedMember
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Clinic Details */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-purple-600" />
                  Clinic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Clinic Name:</span>
                    <p className="font-semibold text-gray-900">{invitation.clinic_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Subdomain:</span>
                    <p className="font-mono text-sm bg-white px-2 py-1 rounded mt-1">
                      {invitation.subdomain}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Admin Name:</span>
                    <p className="font-semibold text-gray-900">{invitation.admin_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="text-gray-900">{invitation.admin_email}</p>
                  </div>
                </div>
              </div>

              {/* Trial Period Badge */}
              <div className="flex items-center justify-center">
                <Badge className="bg-green-500 text-white text-base px-6 py-2">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {invitation.trial_days}-Day Free Trial Included
                </Badge>
              </div>

              {/* What's Included */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">What's Included:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                  {[
                    'Membership Management',
                    'Patient Portal',
                    'Appointment Scheduling',
                    'Payment Processing',
                    'Analytics & Reports',
                    'Email Notifications',
                    'WordPress Integration',
                    '24/7 Support Access'
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Accept Invitation Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-teal-600" />
              Set Your Admin Password
            </CardTitle>
            <CardDescription>
              Create a secure password to access your clinic dashboard
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleAccept} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <p className="text-xs text-gray-500">
                  Must be at least 8 characters long
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-lg py-6"
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Accept Invitation & Get Started
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500 mt-4">
                By accepting, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
